import React, { useState } from "react";

import ColorPicker from "components/basic/color-picker/ColorPicker";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { T } from "services/lang/LanguageService";
import { ScadaEditorProps, ScadaLabel, TagHighlight } from "../scadaTypes";
import { numberConditions } from "../utils/const";

type ScadaTagEditorProps = ScadaEditorProps & {
  tag?: ScadaLabel;
  onClose: () => void;
};

type TagOptions = Pick<ScadaLabel, "isRound" | "roundSize">

const defaultHighlight: TagHighlight = {
  value: 0,
  condition: "gte",
  bgColor: "#FF0000",
  textColor: "#000000",
};

const ScadaTagEditor: React.FC<ScadaTagEditorProps> = ({ tag, onClose, config, onConfigChange }) => {
  const [highlights, setHighlights] = useState<TagHighlight[]>(() =>
    tag?.highlights?.length ? tag.highlights : [defaultHighlight]
  );
  const [tagOptions, setTagOptions] = useState<TagOptions>(() => ({
    isRound: tag?.isRound ?? false,
    roundSize: tag?.roundSize ?? 36,
  }))

  const handleHighlightsChange = (
    value: string | number | null,
    field: keyof TagHighlight,
    index: number
  ) => {
    setHighlights(prev => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  };

  const handleSave = () => {
    onConfigChange({
      ...config,
      elements: config.elements.map(el =>
        el.identifier === tag?.identifier ? { ...el, highlights, ...tagOptions } : el
      ),
    });

    onClose();
  }

  const renderNumInput = (value: number, index: number) => {
    return (
      <InputNumber
        value={value}
        onChange={e => handleHighlightsChange(e.value, "value", index)}
        maxFractionDigits={5}
        useGrouping={false}
        placeholder={T("word.value")}
      />
    );
  };

  const renderDropdown = (value: string, index: number) => {
    return (
      <Dropdown
        value={value}
        options={numberConditions}
        optionValue="value"
        onChange={e => handleHighlightsChange(e.value, "condition", index)}
      />
    );
  };

  const renderColorPicker = (color: string, field: keyof TagHighlight, index: number) => {
    return (
      <ColorPicker
        value={color}
        onValueChange={e => handleHighlightsChange(e.target.value, field, index)}
      />
    );
  };

  const renderActions = (_rowData: TagHighlight, options: ColumnBodyOptions) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-danger p-button-rounded p-button-raised p-button-text"
        onClick={() => setHighlights(prev => prev.toSpliced(options.rowIndex, 1))}
      />
    );
  };

  const columns: TableColumn<TagHighlight>[] = [
    {
      field: "value",
      header: T("word.value"),
      columnKey: "value",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderNumInput(rowData.value, options.rowIndex),
    },
    {
      field: "condition",
      header: T("word.condition"),
      columnKey: "condition",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderDropdown(rowData.condition, options.rowIndex),
    },
    {
      field: "bgColor",
      header: T("word.background-color"),
      columnKey: "bgColor",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderColorPicker(rowData.bgColor, "bgColor", options.rowIndex),
    },
    {
      field: "textColor",
      header: T("word.text-color"),
      columnKey: "textColor",
      body: (rowData: TagHighlight, options: ColumnBodyOptions) =>
        renderColorPicker(rowData.textColor, "textColor", options.rowIndex),
    },
  ];
  return (
    <div className="flex flex-column gap-3">
      <div className="flex">
        <InputLabelWrapper labelName={T("word.round")} col={2}>
          <InputSwitch
            checked={tagOptions.isRound}
            onChange={e => setTagOptions(prev => ({ ...prev, isRound: e.value }))}
            className="mt-2"
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName={T("word.round-size")} col={3}>
          <InputNumber
            value={tagOptions.roundSize}
            onChange={e => setTagOptions(prev => ({ ...prev, roundSize: e.value! }))}
            maxFractionDigits={1}
            useGrouping={false}
            placeholder={T("word.round-size")}
            suffix=" px"
            disabled={!tagOptions.isRound}
          />
        </InputLabelWrapper>
      </div>
      <DataTable value={highlights}>
        {columns.map(col => (
          <Column key={col.field} {...col} />
        ))}
        <Column columnKey="actions" body={renderActions} />
      </DataTable>
      <Button
        type="button"
        title={T("action.add-new-highlight-row")}
        icon="pi pi-plus"
        className="p-button-outlined p-button-rounded p-button-success mt-3 ml-3"
        onClick={() => setHighlights([...highlights, defaultHighlight])}
      />
      <div className="flex justify-content-end">
        <Button
          type="button"
          label={T("action.save")}
          className="p-button-success mt-3 ml-3"
          onClick={handleSave}
        />
        <Button
          type="button"
          label={T("action.close")}
          className="p-button-outlined p-button-secondary mt-3 ml-3"
          onClick={onClose}
        />
      </div>
    </div>
  );
};

export default ScadaTagEditor;
